import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiUrl } from '../apiConfig';

// Fields that the bulk-edit modal exposes as "shared" toggles.
// Keep this list in sync with _BULK_SCALAR_FIELDS on the backend.
const SHARED_EDITABLE_FIELDS = [
  { key: 'price',              label: 'Price',       type: 'number', step: '0.01' },
  { key: 'inventory_quantity', label: 'Stock',       type: 'number', step: '1', min: '0' },
  { key: 'description',        label: 'Description', type: 'textarea' },
  { key: 'image_url',          label: 'Image URL',   type: 'text' },
  { key: 'product_url',        label: 'Product URL', type: 'text' },
];

const Products = () => {
  const { user, checkAuth } = useAuth();

  // ---- Data ----------------------------------------------------------
  const [products, setProducts] = useState([]); // grouped products from the API
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [csrfToken, setCsrfToken] = useState('');

  // ---- UI / interaction ---------------------------------------------
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [editingItem, setEditingItem] = useState(null);            // single-variant modal
  const [editingProduct, setEditingProduct] = useState(null);      // bulk product modal
  const [deleteConfirm, setDeleteConfirm] = useState(null);        // single-variant delete
  const [deleteProductConfirm, setDeleteProductConfirm] = useState(null); // entire-product delete
  const [message, setMessage] = useState({ text: '', type: '', visible: false });

  // ---- Filters / search ---------------------------------------------
  const [searchQuery, setSearchQuery] = useState('');
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({ roomTypes: [], furnitureTypes: [], colorSchemes: [] });
  const [activeFilters, setActiveFilters] = useState({ roomTypes: [], furnitureTypes: [], colorSchemes: [] });

  // ---- Filter option sources ----------------------------------------
  const [roomTypes, setRoomTypes] = useState([]);
  const [furnitureTypes, setFurnitureTypes] = useState([]);
  const [colorSchemes, setColorSchemes] = useState([]);

  // ===================================================================
  // Bootstrapping
  // ===================================================================

  useEffect(() => {
    const getCsrfToken = async () => {
      try {
        const response = await fetch(apiUrl('/api/get-csrf-token/'), { credentials: 'include' });
        const data = await response.json();
        setCsrfToken(data.csrfToken);
      } catch (err) {
        console.error('Failed to get CSRF token:', err);
      }
    };
    getCsrfToken();
  }, []);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [roomTypesRes, furnitureTypesRes, colorSchemesRes] = await Promise.all([
          fetch(apiUrl('/api/room-types/'), { credentials: 'include' }),
          fetch(apiUrl('/api/furniture-types/'), { credentials: 'include' }),
          fetch(apiUrl('/api/color-schemes/'), { credentials: 'include' }),
        ]);
        if (roomTypesRes.ok && furnitureTypesRes.ok && colorSchemesRes.ok) {
          const [roomTypesData, furnitureTypesData, colorSchemesData] = await Promise.all([
            roomTypesRes.json(), furnitureTypesRes.json(), colorSchemesRes.json(),
          ]);
          setRoomTypes(roomTypesData);
          setFurnitureTypes(furnitureTypesData.types || []);
          setColorSchemes(colorSchemesData.schemes || []);
        }
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
      }
    };
    if (user) fetchFilterOptions();
  }, [user]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl('/api/furniture-items/grouped/'), {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 401) {
        showMessage('Session expired. Please log in again.', 'error');
        checkAuth();
        return;
      }
      if (!response.ok) throw new Error('Failed to fetch products');

      const data = await response.json();
      setProducts(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      showMessage('Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  }, [checkAuth]);

  useEffect(() => {
    if (user) fetchProducts();
  }, [user, fetchProducts]);

  // ===================================================================
  // Filtering (operates on grouped products: a product matches if ANY
  // of its variants matches every active filter axis.)
  // ===================================================================

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return products.filter((product) => {
      const variants = product.variants || [];

      if (q) {
        const productNameMatch = (product.name || '').toLowerCase().includes(q);
        const variantMatch = variants.some(v =>
          (v.name || '').toLowerCase().includes(q) ||
          (v.variant_name || '').toLowerCase().includes(q)
        );
        if (!productNameMatch && !variantMatch) return false;
      }

      if (activeFilters.furnitureTypes.length > 0) {
        const ok = variants.some(v => activeFilters.furnitureTypes.includes(v.furniture_type));
        if (!ok) return false;
      }

      if (activeFilters.colorSchemes.length > 0) {
        const ok = variants.some(v => activeFilters.colorSchemes.includes(v.color_scheme));
        if (!ok) return false;
      }

      // Room types are not directly serialized on items today; ignore for now
      // (same behaviour as the previous version of this page).
      return true;
    });
  }, [products, searchQuery, activeFilters]);

  const totalVariantCount = useMemo(
    () => products.reduce((sum, p) => sum + (p.variant_count || 0), 0),
    [products]
  );
  const visibleVariantCount = useMemo(
    () => filteredProducts.reduce((sum, p) => sum + (p.variant_count || 0), 0),
    [filteredProducts]
  );

  // ===================================================================
  // Helpers
  // ===================================================================

  const showMessage = (text, type) => {
    setMessage({ text, type, visible: true });
    setTimeout(() => setMessage(prev => ({ ...prev, visible: false })), 3500);
  };

  const formatPriceRange = (product) => {
    const lo = product.price_min;
    const hi = product.price_max;
    if (lo == null && hi == null) return '—';
    if (lo === hi) return `$${parseFloat(lo).toFixed(2)}`;
    return `$${parseFloat(lo).toFixed(2)} – $${parseFloat(hi).toFixed(2)}`;
  };

  const toggleExpanded = (key) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleFilter = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(v => v !== value)
        : [...prev[filterType], value],
    }));
  };

  const handleApplyFilters = () => {
    setActiveFilters(filters);
    setFilterModalOpen(false);
    showMessage('Filters applied', 'success');
  };

  const handleClearFilters = () => {
    const empty = { roomTypes: [], furnitureTypes: [], colorSchemes: [] };
    setFilters(empty);
    setActiveFilters(empty);
    setSearchQuery('');
    showMessage('Filters cleared', 'success');
  };

  const removeActiveFilter = (filterType, value) => {
    const updated = { ...activeFilters, [filterType]: activeFilters[filterType].filter(v => v !== value) };
    setActiveFilters(updated);
    setFilters(updated);
  };

  const getActiveFilterCount = () =>
    activeFilters.roomTypes.length + activeFilters.furnitureTypes.length + activeFilters.colorSchemes.length;

  // ===================================================================
  // Single-variant edit (preserved from previous version)
  // ===================================================================

  const handleEditVariant = (variant) => {
    // Open the modal immediately with what we have, then lazy-fetch live
    // Shopify inventory in the background. We only fetch when the DB row
    // is "unclaimed" (inventory_quantity is null) so we can prefill from
    // Shopify without forcing the admin to type a number blind.
    const initialInventory = variant.inventory_quantity == null
      ? ''
      : String(variant.inventory_quantity);
    setEditingItem({
      ...variant,
      original: { ...variant },
      inventory_quantity_input: initialInventory,
      inventoryClaimed: variant.inventory_quantity != null,
      inventoryLoading: variant.inventory_quantity == null,
      inventoryLive: null,
      inventoryTracked: null,
      inventoryError: null,
    });

    if (variant.inventory_quantity == null) {
      (async () => {
        try {
          const res = await fetch(
            apiUrl(`/api/furniture-items/${variant.id}/shopify-inventory/`),
            { credentials: 'include' }
          );
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || err.error || `Failed (${res.status})`);
          }
          const data = await res.json();
          setEditingItem(prev => {
            if (!prev || prev.id !== variant.id) return prev;
            // If the admin already typed something, don't clobber it.
            const liveStr = data.live_quantity == null ? '' : String(data.live_quantity);
            return {
              ...prev,
              inventoryLoading: false,
              inventoryLive: data.live_quantity,
              inventoryTracked: data.tracked,
              inventory_quantity_input:
                prev.inventory_quantity_input === '' ? liveStr : prev.inventory_quantity_input,
            };
          });
        } catch (err) {
          setEditingItem(prev => {
            if (!prev || prev.id !== variant.id) return prev;
            return { ...prev, inventoryLoading: false, inventoryError: err.message };
          });
        }
      })();
    }
  };
  const handleCancelEdit = () => setEditingItem(null);
  const handleVariantInputChange = (field, value) =>
    setEditingItem(prev => ({ ...prev, [field]: value }));

  const handleSaveVariant = async () => {
    // Inventory is handled through the bulk-update endpoint so the change
    // propagates to Shopify (the regular PATCH would only touch the DB).
    // Everything else still goes through the per-item PATCH to preserve
    // the existing behavior.
    const original = editingItem.original || {};
    const origInvStr = original.inventory_quantity == null
      ? ''
      : String(original.inventory_quantity);
    const draftInvStr = String(editingItem.inventory_quantity_input ?? '').trim();
    const inventoryChanged = draftInvStr !== origInvStr;

    if (inventoryChanged) {
      if (draftInvStr === '') {
        showMessage('Stock cannot be cleared once set.', 'error');
        return;
      }
      const parsedInv = Number(draftInvStr);
      if (!Number.isInteger(parsedInv) || parsedInv < 0) {
        showMessage('Stock must be a whole non-negative number.', 'error');
        return;
      }
    }

    try {
      // 1) Persist the non-inventory fields (existing path).
      const response = await fetch(apiUrl(`/api/furniture-items/${editingItem.id}/`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingItem.name,
          description: editingItem.description,
          price: parseFloat(editingItem.price),
          image_url: editingItem.image_url,
          product_url: editingItem.product_url,
        }),
      });

      if (response.status === 401) {
        showMessage('Session expired. Please log in again.', 'error');
        checkAuth();
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update item');
      }

      // 2) If inventory changed, push it through bulk-update so the Shopify
      //    sync helper runs and the compareQuantity guard kicks in.
      let inventoryWarning = null;
      if (inventoryChanged) {
        const invResp = await fetch(apiUrl('/api/furniture-items/bulk-update/'), {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            item_ids: [editingItem.id],
            shared: { inventory_quantity: Number(draftInvStr) },
            overrides: [],
          }),
        });

        if (invResp.status === 401) {
          showMessage('Session expired. Please log in again.', 'error');
          checkAuth();
          return;
        }
        if (invResp.status === 403) {
          inventoryWarning = 'Stock not synced — superuser permission required.';
        } else if (!invResp.ok) {
          const errorData = await invResp.json().catch(() => ({}));
          inventoryWarning = errorData.detail || errorData.error || 'Stock update failed.';
        } else {
          const invResult = await invResp.json();
          const invSync = invResult.inventory_sync;
          if (invSync && invSync.attempted && !invSync.success) {
            const failed = (invSync.items || []).filter(p => !p.success);
            const reason = failed[0]?.error || 'see Reconciliation';
            inventoryWarning = `Stock saved to DB but Shopify sync failed: ${reason}`;
          }
        }
      }

      setEditingItem(null);
      if (inventoryWarning) {
        showMessage(inventoryWarning, 'warning');
      } else {
        showMessage(
          inventoryChanged ? 'Variant and stock updated' : 'Variant updated successfully',
          'success'
        );
      }
      fetchProducts();
    } catch (err) {
      console.error('Save error:', err);
      showMessage(err.message, 'error');
    }
  };

  // ===================================================================
  // Single-variant delete (preserved)
  // ===================================================================

  const handleDeleteVariant = async (itemId) => {
    try {
      const response = await fetch(apiUrl(`/api/furniture-items/${itemId}/`), {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' },
      });

      if (response.status === 401) {
        showMessage('Session expired. Please log in again.', 'error');
        checkAuth();
        return;
      }
      if (!response.ok) throw new Error('Failed to delete item');

      setDeleteConfirm(null);
      showMessage('Variant deleted successfully', 'success');
      fetchProducts();
    } catch (err) {
      console.error('Delete error:', err);
      showMessage(err.message, 'error');
    }
  };

  // ===================================================================
  // Bulk product delete (entire product = all variants)
  // ===================================================================

  const handleDeleteProduct = async (product) => {
    try {
      const itemIds = (product.variants || []).map(v => v.id);
      if (itemIds.length === 0) {
        setDeleteProductConfirm(null);
        return;
      }
      const response = await fetch(apiUrl('/api/reconciliation/delete-items/'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_ids: itemIds }),
      });

      if (response.status === 401) {
        showMessage('Session expired. Please log in again.', 'error');
        checkAuth();
        return;
      }
      if (response.status === 403) {
        showMessage('You need superuser permission to delete products.', 'error');
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete product');
      }

      const result = await response.json();
      setDeleteProductConfirm(null);
      showMessage(`Deleted product (${result.deleted_count} variants)`, 'success');
      fetchProducts();
    } catch (err) {
      console.error('Delete product error:', err);
      showMessage(err.message, 'error');
    }
  };

  // ===================================================================
  // Bulk product edit
  // ===================================================================

  const openProductEditor = (product) => {
    setEditingProduct({
      product,
      // Each shared field starts disabled; the user opts in per field.
      shared: SHARED_EDITABLE_FIELDS.reduce((acc, f) => {
        acc[f.key] = { enabled: false, value: '' };
        return acc;
      }, {}),
      // Editable per-variant copies; we diff against original at save-time.
      // inventory_quantity may be null on variants the DB hasn't claimed yet —
      // we treat null as "" in the input and only send a value if the admin
      // types one in.
      variantsDraft: (product.variants || []).map(v => ({
        id: v.id,
        name: v.name || '',
        variant_name: v.variant_name || '',
        price: v.price ?? '',
        inventory_quantity: v.inventory_quantity == null ? '' : String(v.inventory_quantity),
        image_url: v.image_url || '',
        product_url: v.product_url || '',
        _original: v,
      })),
      activeTab: 'shared', // 'shared' | 'overrides'
      saving: false,
    });
  };

  const closeProductEditor = () => setEditingProduct(null);

  const updateSharedField = (key, patch) => {
    setEditingProduct(prev => ({
      ...prev,
      shared: { ...prev.shared, [key]: { ...prev.shared[key], ...patch } },
    }));
  };

  const updateVariantDraft = (variantId, field, value) => {
    setEditingProduct(prev => ({
      ...prev,
      variantsDraft: prev.variantsDraft.map(v =>
        v.id === variantId ? { ...v, [field]: value } : v
      ),
    }));
  };

  const buildBulkPayload = () => {
    const product = editingProduct.product;

    const shared = {};
    for (const field of SHARED_EDITABLE_FIELDS) {
      const cell = editingProduct.shared[field.key];
      if (!cell.enabled) continue;
      let value = cell.value;
      if (field.key === 'price') {
        const parsed = parseFloat(value);
        if (Number.isNaN(parsed)) {
          throw new Error(`Shared price must be a valid number`);
        }
        value = parsed;
      } else if (field.key === 'inventory_quantity') {
        if (String(value).trim() === '') {
          throw new Error(`Shared stock must be a whole number`);
        }
        const parsed = Number(value);
        if (!Number.isInteger(parsed) || parsed < 0) {
          throw new Error(`Shared stock must be a whole non-negative number`);
        }
        value = parsed;
      }
      shared[field.key] = value;
    }

    const overrides = [];
    for (const draft of editingProduct.variantsDraft) {
      const original = draft._original;
      const diff = {};

      if (draft.name !== (original.name || '')) diff.name = draft.name;
      if (draft.variant_name !== (original.variant_name || '')) diff.variant_name = draft.variant_name;
      if (draft.image_url !== (original.image_url || '')) diff.image_url = draft.image_url;
      if (draft.product_url !== (original.product_url || '')) diff.product_url = draft.product_url;

      const draftPriceStr = String(draft.price ?? '').trim();
      const origPriceStr = String(original.price ?? '').trim();
      if (draftPriceStr !== origPriceStr) {
        const parsed = parseFloat(draftPriceStr);
        if (Number.isNaN(parsed)) {
          throw new Error(`Variant "${draft.variant_name || draft.name}" has an invalid price`);
        }
        diff.price = parsed;
      }

      // Inventory diff: '' === unclaimed/null; only send if the admin
      // actually changed it. Validate as a non-negative integer when sent.
      const draftInvStr = String(draft.inventory_quantity ?? '').trim();
      const origInvStr = original.inventory_quantity == null
        ? ''
        : String(original.inventory_quantity);
      if (draftInvStr !== origInvStr) {
        if (draftInvStr === '') {
          // We intentionally do NOT support clearing inventory back to NULL
          // from the bulk editor — that would require a separate API path.
          throw new Error(
            `Variant "${draft.variant_name || draft.name}": stock cannot be cleared once set`
          );
        }
        const parsedInv = Number(draftInvStr);
        if (!Number.isInteger(parsedInv) || parsedInv < 0) {
          throw new Error(
            `Variant "${draft.variant_name || draft.name}" has invalid stock`
          );
        }
        diff.inventory_quantity = parsedInv;
      }

      if (Object.keys(diff).length > 0) {
        overrides.push({ id: draft.id, ...diff });
      }
    }

    // Pick the strongest target identifier available.
    const target = {};
    if (product.parent_product_id != null) {
      target.parent_product_id = product.parent_product_id;
    } else if (product.shopify_product_id) {
      target.shopify_product_id = product.shopify_product_id;
    } else {
      target.item_ids = (product.variants || []).map(v => v.id);
    }

    return { ...target, shared, overrides };
  };

  const handleSaveProduct = async () => {
    let payload;
    try {
      payload = buildBulkPayload();
    } catch (err) {
      showMessage(err.message, 'error');
      return;
    }

    if (Object.keys(payload.shared).length === 0 && payload.overrides.length === 0) {
      showMessage('Nothing to save — no shared fields enabled and no per-variant changes.', 'info');
      return;
    }

    try {
      setEditingProduct(prev => ({ ...prev, saving: true }));
      const response = await fetch(apiUrl('/api/furniture-items/bulk-update/'), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        showMessage('Session expired. Please log in again.', 'error');
        checkAuth();
        return;
      }
      if (response.status === 403) {
        showMessage('You need superuser permission to bulk-edit products.', 'error');
        setEditingProduct(prev => ({ ...prev, saving: false }));
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || 'Bulk update failed');
      }

      const result = await response.json();

      let summary = `Updated ${result.updated_count} variant(s); ${result.unchanged_count} unchanged.`;
      const priceSync = result.shopify_sync;
      const invSync = result.inventory_sync;
      const priceFailed = priceSync && priceSync.attempted && !priceSync.success;
      const invFailed = invSync && invSync.attempted && !invSync.success;

      if (priceSync && priceSync.attempted) {
        if (priceSync.success) {
          summary += ' Shopify prices synced.';
        } else {
          const failed = (priceSync.products || []).filter(p => !p.success).length;
          summary += ` Shopify price sync failed for ${failed} product(s) — open Reconciliation to repair.`;
        }
      }
      if (invSync && invSync.attempted) {
        if (invSync.success) {
          summary += ' Shopify stock synced.';
        } else {
          const failed = (invSync.items || []).filter(p => !p.success).length;
          summary += ` Shopify stock sync failed for ${failed} item(s) — open Reconciliation to repair.`;
        }
      }

      showMessage(summary, priceFailed || invFailed ? 'warning' : 'success');
      setEditingProduct(null);
      fetchProducts();
    } catch (err) {
      console.error('Bulk save error:', err);
      showMessage(err.message, 'error');
      setEditingProduct(prev => prev ? { ...prev, saving: false } : prev);
    }
  };

  const editorAffectedCount = editingProduct ? editingProduct.variantsDraft.length : 0;
  const editorHasSharedEnabled = editingProduct
    ? Object.values(editingProduct.shared).some(c => c.enabled)
    : false;
  const isVariantDirty = (d) => {
    const o = d._original;
    const origInvStr = o.inventory_quantity == null ? '' : String(o.inventory_quantity);
    return (
      d.name !== (o.name || '') ||
      d.variant_name !== (o.variant_name || '') ||
      d.image_url !== (o.image_url || '') ||
      d.product_url !== (o.product_url || '') ||
      String(d.price ?? '').trim() !== String(o.price ?? '').trim() ||
      String(d.inventory_quantity ?? '').trim() !== origInvStr
    );
  };
  const editorOverrideCount = editingProduct
    ? editingProduct.variantsDraft.filter(isVariantDirty).length
    : 0;

  // ===================================================================
  // Render
  // ===================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2C2C2C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#666]">Loading products...</p>
        </div>
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500 mb-4">Error: {error}</p>
          <button onClick={fetchProducts} className="px-4 py-2 bg-[#2C2C2C] text-white rounded-lg hover:bg-[#1a1a1a]">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Toast */}
      {message.visible && (
        <div className={`fixed top-24 right-8 px-6 py-4 rounded-xl shadow-lg ${
          message.type === 'error'   ? 'bg-red-500 text-white'    :
          message.type === 'warning' ? 'bg-amber-500 text-white'  :
          message.type === 'success' ? 'bg-green-500 text-white'  :
                                       'bg-[#2C2C2C] text-white'
        } text-sm font-medium z-50 animate-fade-in max-w-md`}>
          {message.text}
        </div>
      )}

      {/* Filter modal */}
      {filterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold text-[#2C2C2C]">Filter Products</h3>
              <button onClick={() => setFilterModalOpen(false)} className="text-[#666] hover:text-[#2C2C2C]">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-[#2C2C2C] mb-3">Furniture Type</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {furnitureTypes.map((type) => (
                    <label key={type.id} className="flex items-center space-x-3 cursor-pointer hover:bg-[#F5F4F2] p-2 rounded">
                      <input
                        type="checkbox"
                        checked={filters.furnitureTypes.includes(type.id)}
                        onChange={() => toggleFilter('furnitureTypes', type.id)}
                        className="w-4 h-4 text-[#2C2C2C] focus:ring-[#2C2C2C] rounded"
                      />
                      <span className="text-[#666]">{type.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-[#2C2C2C] mb-3">Color Scheme</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {colorSchemes.map((scheme) => (
                    <label key={scheme.id} className="flex items-center space-x-3 cursor-pointer hover:bg-[#F5F4F2] p-2 rounded">
                      <input
                        type="checkbox"
                        checked={filters.colorSchemes.includes(scheme.id)}
                        onChange={() => toggleFilter('colorSchemes', scheme.id)}
                        className="w-4 h-4 text-[#2C2C2C] focus:ring-[#2C2C2C] rounded"
                      />
                      <span className="text-[#666]">{scheme.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-[#2C2C2C] mb-3">Room Type</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {roomTypes.map((type) => (
                    <label key={type.id} className="flex items-center space-x-3 cursor-pointer hover:bg-[#F5F4F2] p-2 rounded">
                      <input
                        type="checkbox"
                        checked={filters.roomTypes.includes(type.id)}
                        onChange={() => toggleFilter('roomTypes', type.id)}
                        className="w-4 h-4 text-[#2C2C2C] focus:ring-[#2C2C2C] rounded"
                      />
                      <span className="text-[#666]">{type.type}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={handleApplyFilters}
                className="flex-1 bg-[#2C2C2C] text-white py-3 px-6 rounded-lg hover:bg-[#1a1a1a] transition-colors font-medium"
              >
                Apply Filters
              </button>
              <button
                onClick={() => {
                  setFilters({ roomTypes: [], furnitureTypes: [], colorSchemes: [] });
                  setFilterModalOpen(false);
                }}
                className="flex-1 bg-[#E8E6E1] text-[#2C2C2C] py-3 px-6 rounded-lg hover:bg-[#D1CFC8] transition-colors font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single-variant delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md">
            <h3 className="text-2xl font-semibold text-[#2C2C2C] mb-4">Delete Variant</h3>
            <p className="text-[#666] mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => handleDeleteVariant(deleteConfirm.id)}
                className="flex-1 bg-red-500 text-white py-3 px-6 rounded-lg hover:bg-red-600 transition-colors font-medium"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 bg-[#E8E6E1] text-[#2C2C2C] py-3 px-6 rounded-lg hover:bg-[#D1CFC8] transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Whole-product delete confirm */}
      {deleteProductConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md">
            <h3 className="text-2xl font-semibold text-[#2C2C2C] mb-4">Delete Entire Product</h3>
            <p className="text-[#666] mb-6">
              This will delete <strong>{deleteProductConfirm.name}</strong> and all
              <strong> {deleteProductConfirm.variant_count} variants</strong>. This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => handleDeleteProduct(deleteProductConfirm)}
                className="flex-1 bg-red-500 text-white py-3 px-6 rounded-lg hover:bg-red-600 transition-colors font-medium"
              >
                Delete Product
              </button>
              <button
                onClick={() => setDeleteProductConfirm(null)}
                className="flex-1 bg-[#E8E6E1] text-[#2C2C2C] py-3 px-6 rounded-lg hover:bg-[#D1CFC8] transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-variant edit modal (preserved) */}
      {editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-semibold text-[#2C2C2C] mb-6">Edit Variant</h3>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Name</label>
                <input
                  type="text"
                  value={editingItem.name}
                  onChange={(e) => handleVariantInputChange('name', e.target.value)}
                  className="w-full px-4 py-3 border border-[#D1CFC8] rounded-lg focus:outline-none focus:border-[#2C2C2C] transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Description</label>
                <textarea
                  value={editingItem.description || ''}
                  onChange={(e) => handleVariantInputChange('description', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-[#D1CFC8] rounded-lg focus:outline-none focus:border-[#2C2C2C] transition-colors resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Price</label>
                <input
                  type="number"
                  step="0.01"
                  value={editingItem.price}
                  onChange={(e) => handleVariantInputChange('price', e.target.value)}
                  className="w-full px-4 py-3 border border-[#D1CFC8] rounded-lg focus:outline-none focus:border-[#2C2C2C] transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Stock
                  {!editingItem.inventoryClaimed && (
                    <span className="ml-2 text-xs font-normal text-amber-600">
                      Not yet tracked in DB — saving claims it as the source of truth.
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={editingItem.inventory_quantity_input ?? ''}
                  onChange={(e) => handleVariantInputChange('inventory_quantity_input', e.target.value)}
                  placeholder={editingItem.inventoryLoading ? 'Loading from Shopify…' : 'Stock quantity'}
                  disabled={editingItem.inventoryLoading}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-[#2C2C2C] transition-colors ${
                    editingItem.inventoryClaimed ? 'border-[#D1CFC8]' : 'border-dashed border-[#D1CFC8] bg-[#FAF9F7]'
                  } disabled:opacity-60`}
                />
                {editingItem.inventoryLoading && (
                  <p className="text-xs text-[#999] mt-2">Fetching live stock from Shopify…</p>
                )}
                {!editingItem.inventoryLoading && editingItem.inventoryError && (
                  <p className="text-xs text-red-500 mt-2">
                    Couldn't fetch live Shopify stock: {editingItem.inventoryError}. You can still type a value to set it.
                  </p>
                )}
                {!editingItem.inventoryLoading && !editingItem.inventoryError && !editingItem.inventoryClaimed && editingItem.inventoryLive != null && (
                  <p className="text-xs text-[#666] mt-2">
                    Live Shopify quantity: <strong>{editingItem.inventoryLive}</strong>
                    {editingItem.inventoryTracked === false && (
                      <span className="ml-2 text-red-500">
                        (tracking is disabled in Shopify — enable it before saving)
                      </span>
                    )}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Image URL</label>
                <input
                  type="text"
                  value={editingItem.image_url || ''}
                  onChange={(e) => handleVariantInputChange('image_url', e.target.value)}
                  className="w-full px-4 py-3 border border-[#D1CFC8] rounded-lg focus:outline-none focus:border-[#2C2C2C] transition-colors"
                />
                {editingItem.image_url && (
                  <div className="mt-3">
                    <img
                      src={editingItem.image_url}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg border border-[#E8E6E1]"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Invalid+Image'; }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Product URL</label>
                <input
                  type="text"
                  value={editingItem.product_url || ''}
                  onChange={(e) => handleVariantInputChange('product_url', e.target.value)}
                  placeholder="https://example.com/product"
                  className="w-full px-4 py-3 border border-[#D1CFC8] rounded-lg focus:outline-none focus:border-[#2C2C2C] transition-colors"
                />
              </div>

              <div className="bg-[#F5F4F2] p-4 rounded-lg space-y-2">
                <p className="text-sm text-[#666]"><strong>Type:</strong> {editingItem.furniture_type_name || 'N/A'}</p>
                <p className="text-sm text-[#666]"><strong>Style:</strong> {editingItem.style_name || 'N/A'}</p>
                <p className="text-sm text-[#666]"><strong>Color Scheme:</strong> {editingItem.color_scheme_name || 'N/A'}</p>
                {editingItem.variant_name && (
                  <p className="text-sm text-[#666]"><strong>Variant:</strong> {editingItem.variant_name}</p>
                )}
              </div>
            </div>

            <div className="flex space-x-4 mt-8">
              <button onClick={handleSaveVariant} className="flex-1 bg-[#2C2C2C] text-white py-3 px-6 rounded-lg hover:bg-[#1a1a1a] transition-colors font-medium">
                Save Changes
              </button>
              <button onClick={handleCancelEdit} className="flex-1 bg-[#E8E6E1] text-[#2C2C2C] py-3 px-6 rounded-lg hover:bg-[#D1CFC8] transition-colors font-medium">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk product editor */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-5xl w-full max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-semibold text-[#2C2C2C]">Edit Product</h3>
                <p className="text-[#666] mt-1">{editingProduct.product.name}</p>
                <p className="text-[#999] text-sm mt-1">
                  {editorAffectedCount} variant{editorAffectedCount === 1 ? '' : 's'} in this product
                </p>
              </div>
              <button
                onClick={closeProductEditor}
                disabled={editingProduct.saving}
                className="text-[#666] hover:text-[#2C2C2C] disabled:opacity-50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-[#E8E6E1] mb-6">
              <button
                onClick={() => setEditingProduct(prev => ({ ...prev, activeTab: 'shared' }))}
                className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                  editingProduct.activeTab === 'shared'
                    ? 'border-[#2C2C2C] text-[#2C2C2C]'
                    : 'border-transparent text-[#999] hover:text-[#666]'
                }`}
              >
                Shared (apply to all)
              </button>
              <button
                onClick={() => setEditingProduct(prev => ({ ...prev, activeTab: 'overrides' }))}
                className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                  editingProduct.activeTab === 'overrides'
                    ? 'border-[#2C2C2C] text-[#2C2C2C]'
                    : 'border-transparent text-[#999] hover:text-[#666]'
                }`}
              >
                Per-variant overrides{editorOverrideCount > 0 ? ` (${editorOverrideCount})` : ''}
              </button>
            </div>

            {/* Shared tab */}
            {editingProduct.activeTab === 'shared' && (
              <div className="space-y-5">
                <p className="text-sm text-[#666] bg-[#F5F4F2] p-4 rounded-lg">
                  Tick the checkbox next to a field to apply that value to <strong>all {editorAffectedCount} variants</strong>.
                  Untouched fields are left alone. Per-variant overrides on the next tab take precedence over these shared values.
                </p>

                {SHARED_EDITABLE_FIELDS.map((field) => {
                  const cell = editingProduct.shared[field.key];
                  return (
                    <div key={field.key} className="border border-[#E8E6E1] rounded-lg p-4">
                      <label className="flex items-center space-x-3 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={cell.enabled}
                          onChange={(e) => updateSharedField(field.key, { enabled: e.target.checked })}
                          className="w-4 h-4 text-[#2C2C2C] focus:ring-[#2C2C2C] rounded"
                        />
                        <span className="font-medium text-[#2C2C2C]">{field.label}</span>
                        <span className="text-xs text-[#999]">— apply to all variants</span>
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea
                          value={cell.value}
                          disabled={!cell.enabled}
                          onChange={(e) => updateSharedField(field.key, { value: e.target.value })}
                          rows={3}
                          className="w-full px-4 py-3 border border-[#D1CFC8] rounded-lg focus:outline-none focus:border-[#2C2C2C] transition-colors resize-none disabled:bg-[#F5F4F2] disabled:text-[#999]"
                          placeholder={`New ${field.label.toLowerCase()} for every variant...`}
                        />
                      ) : (
                        <input
                          type={field.type}
                          step={field.step}
                          min={field.min}
                          value={cell.value}
                          disabled={!cell.enabled}
                          onChange={(e) => updateSharedField(field.key, { value: e.target.value })}
                          className="w-full px-4 py-3 border border-[#D1CFC8] rounded-lg focus:outline-none focus:border-[#2C2C2C] transition-colors disabled:bg-[#F5F4F2] disabled:text-[#999]"
                          placeholder={`New ${field.label.toLowerCase()} for every variant...`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Per-variant overrides tab */}
            {editingProduct.activeTab === 'overrides' && (
              <div>
                <p className="text-sm text-[#666] bg-[#F5F4F2] p-4 rounded-lg mb-4">
                  Edit any cell to override the shared value for that one variant. Only rows you change will be sent to the server.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-[#E8E6E1] text-[#2C2C2C]">
                        <th className="py-3 px-2 w-16">Image</th>
                        <th className="py-3 px-2">Variant</th>
                        <th className="py-3 px-2">Full Name</th>
                        <th className="py-3 px-2 w-28">Price</th>
                        <th className="py-3 px-2 w-24">Stock</th>
                        <th className="py-3 px-2">Image URL</th>
                        <th className="py-3 px-2">Product URL</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8E6E1]">
                      {editingProduct.variantsDraft.map((draft) => {
                        const isDirty = isVariantDirty(draft);
                        const stockUnclaimed = draft._original.inventory_quantity == null;
                        return (
                          <tr key={draft.id} className={isDirty ? 'bg-amber-50' : ''}>
                            <td className="py-2 px-2">
                              {draft.image_url ? (
                                <img
                                  src={draft.image_url}
                                  alt=""
                                  className="w-12 h-12 object-cover rounded border border-[#E8E6E1]"
                                  onError={(e) => { e.target.src = 'https://via.placeholder.com/48?text=?'; }}
                                />
                              ) : (
                                <div className="w-12 h-12 bg-[#F5F4F2] rounded border border-[#E8E6E1]" />
                              )}
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="text"
                                value={draft.variant_name}
                                onChange={(e) => updateVariantDraft(draft.id, 'variant_name', e.target.value)}
                                className="w-full px-2 py-1 border border-[#D1CFC8] rounded focus:outline-none focus:border-[#2C2C2C]"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="text"
                                value={draft.name}
                                onChange={(e) => updateVariantDraft(draft.id, 'name', e.target.value)}
                                className="w-full px-2 py-1 border border-[#D1CFC8] rounded focus:outline-none focus:border-[#2C2C2C]"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                step="0.01"
                                value={draft.price}
                                onChange={(e) => updateVariantDraft(draft.id, 'price', e.target.value)}
                                className="w-full px-2 py-1 border border-[#D1CFC8] rounded focus:outline-none focus:border-[#2C2C2C]"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                step="1"
                                min="0"
                                value={draft.inventory_quantity}
                                onChange={(e) => updateVariantDraft(draft.id, 'inventory_quantity', e.target.value)}
                                placeholder={stockUnclaimed ? 'Set stock…' : ''}
                                title={stockUnclaimed
                                  ? 'Stock not yet tracked in DB. Type a number to claim it as the source of truth and push to Shopify.'
                                  : 'Current DB-tracked stock. Edit to push a new value to Shopify.'}
                                className={`w-full px-2 py-1 border rounded focus:outline-none focus:border-[#2C2C2C] ${
                                  stockUnclaimed ? 'border-dashed border-[#D1CFC8] bg-[#FAF9F7]' : 'border-[#D1CFC8]'
                                }`}
                              />
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="text"
                                value={draft.image_url}
                                onChange={(e) => updateVariantDraft(draft.id, 'image_url', e.target.value)}
                                className="w-full px-2 py-1 border border-[#D1CFC8] rounded focus:outline-none focus:border-[#2C2C2C]"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="text"
                                value={draft.product_url}
                                onChange={(e) => updateVariantDraft(draft.id, 'product_url', e.target.value)}
                                className="w-full px-2 py-1 border border-[#D1CFC8] rounded focus:outline-none focus:border-[#2C2C2C]"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between mt-8 pt-4 border-t border-[#E8E6E1]">
              <p className="text-sm text-[#666]">
                {editorHasSharedEnabled || editorOverrideCount > 0
                  ? `Will save: ${Object.values(editingProduct.shared).filter(c => c.enabled).length} shared field(s), ${editorOverrideCount} per-variant override(s)`
                  : 'No changes yet — enable a shared field or edit a variant cell.'}
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={closeProductEditor}
                  disabled={editingProduct.saving}
                  className="px-6 py-3 bg-[#E8E6E1] text-[#2C2C2C] rounded-lg hover:bg-[#D1CFC8] transition-colors font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProduct}
                  disabled={editingProduct.saving || (!editorHasSharedEnabled && editorOverrideCount === 0)}
                  className="px-6 py-3 bg-[#2C2C2C] text-white rounded-lg hover:bg-[#1a1a1a] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingProduct.saving ? 'Saving...' : 'Save All Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page header */}
      <div className="mb-8">
        <h2 className="text-4xl font-light text-[#2C2C2C] tracking-tight mb-2">PRODUCTS</h2>
        <p className="text-[#666] text-lg">Manage your furniture catalog</p>
      </div>

      {/* Search + filter bar */}
      <div className="mb-6 flex items-center space-x-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search products by name or variant..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 pl-12 border border-[#D1CFC8] rounded-lg focus:outline-none focus:border-[#2C2C2C] transition-colors"
          />
          <svg className="w-5 h-5 text-[#666] absolute left-4 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#666] hover:text-[#2C2C2C]"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <button
          onClick={() => setFilterModalOpen(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-[#2C2C2C] text-white rounded-lg hover:bg-[#1a1a1a] transition-colors font-medium relative"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <span>Filter</span>
          {getActiveFilterCount() > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
              {getActiveFilterCount()}
            </span>
          )}
        </button>

        {(getActiveFilterCount() > 0 || searchQuery) && (
          <button
            onClick={handleClearFilters}
            className="px-4 py-3 text-[#666] hover:text-[#2C2C2C] transition-colors font-medium"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Active filters pills */}
      {getActiveFilterCount() > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {activeFilters.furnitureTypes.map(id => {
            const type = furnitureTypes.find(t => t.id === id);
            return type && (
              <span key={`ft-${id}`} className="inline-flex items-center space-x-2 bg-[#F5F4F2] text-[#2C2C2C] px-3 py-1 rounded-full text-sm">
                <span>{type.name}</span>
                <button onClick={() => removeActiveFilter('furnitureTypes', id)} className="hover:text-red-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            );
          })}
          {activeFilters.colorSchemes.map(id => {
            const scheme = colorSchemes.find(s => s.id === id);
            return scheme && (
              <span key={`cs-${id}`} className="inline-flex items-center space-x-2 bg-[#F5F4F2] text-[#2C2C2C] px-3 py-1 rounded-full text-sm">
                <span>{scheme.name}</span>
                <button onClick={() => removeActiveFilter('colorSchemes', id)} className="hover:text-red-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Counts */}
      <div className="mb-4">
        <p className="text-[#666] text-sm">
          Showing {filteredProducts.length} of {products.length} products
          {' '}({visibleVariantCount} of {totalVariantCount} variants)
        </p>
      </div>

      {/* Products list */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-[#666] text-lg">
            {products.length === 0
              ? 'No products found. Start by uploading new items.'
              : 'No products match your filters. Try adjusting your search or filters.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg border border-[#E8E6E1] overflow-hidden">
          {/* Header row */}
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-[#F5F4F2] border-b border-[#E8E6E1] font-semibold text-[#2C2C2C] text-sm">
            <div className="col-span-1"></div>
            <div className="col-span-1">Image</div>
            <div className="col-span-3">Product</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-1">Variants</div>
            <div className="col-span-2">Price</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          <div className="divide-y divide-[#E8E6E1]">
            {filteredProducts.map((product) => {
              const isExpanded = expandedKeys.has(product.group_key);
              return (
                <div key={product.group_key}>
                  {/* Product row */}
                  <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-[#F5F4F2] transition-colors">
                    <div className="col-span-1">
                      <button
                        onClick={() => toggleExpanded(product.group_key)}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#E8E6E1] transition-colors"
                        title={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        <svg
                          className={`w-5 h-5 text-[#666] transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>

                    <div className="col-span-1">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg border border-[#E8E6E1]"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/64?text=No+Image'; }}
                        />
                      ) : (
                        <div className="w-16 h-16 bg-[#F5F4F2] rounded-lg border border-[#E8E6E1] flex items-center justify-center">
                          <svg className="w-8 h-8 text-[#D1CFC8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>

                    <div className="col-span-3">
                      <h3 className="font-semibold text-[#2C2C2C] text-base" title={product.name}>
                        {product.name}
                      </h3>
                      {product.shopify_product_id && (
                        <p className="text-xs text-[#999] truncate" title={product.shopify_product_id}>
                          Shopify product
                        </p>
                      )}
                    </div>

                    <div className="col-span-2">
                      <span className="text-[#666] text-sm">{product.furniture_type_name || 'Uncategorized'}</span>
                    </div>

                    <div className="col-span-1">
                      <span className="inline-block bg-[#2C2C2C] text-white text-xs px-3 py-1 rounded-full">
                        {product.variant_count}
                      </span>
                    </div>

                    <div className="col-span-2">
                      <span className="font-bold text-[#2C2C2C] text-base">{formatPriceRange(product)}</span>
                    </div>

                    <div className="col-span-2 flex justify-end space-x-2">
                      <button
                        onClick={() => openProductEditor(product)}
                        className="bg-[#2C2C2C] text-white py-2 px-4 rounded-lg hover:bg-[#1a1a1a] transition-colors text-sm font-medium"
                      >
                        Edit Product
                      </button>
                      <button
                        onClick={() => setDeleteProductConfirm(product)}
                        className="bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
                        title="Delete entire product (all variants)"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Expanded variant rows */}
                  {isExpanded && (
                    <div className="bg-[#FAF9F7] border-t border-[#E8E6E1]">
                      {(product.variants || []).map((variant) => (
                        <div
                          key={variant.id}
                          className="grid grid-cols-12 gap-4 px-6 py-3 items-center border-b border-[#E8E6E1] last:border-b-0 hover:bg-white transition-colors"
                        >
                          <div className="col-span-1"></div>
                          <div className="col-span-1">
                            {variant.image_url ? (
                              <img
                                src={variant.image_url}
                                alt={variant.name}
                                className="w-12 h-12 object-cover rounded border border-[#E8E6E1]"
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/48?text=?'; }}
                              />
                            ) : (
                              <div className="w-12 h-12 bg-[#F5F4F2] rounded border border-[#E8E6E1]" />
                            )}
                          </div>
                          <div className="col-span-3">
                            <p className="text-sm text-[#2C2C2C]" title={variant.name}>{variant.name}</p>
                          </div>
                          <div className="col-span-2">
                            {variant.variant_name ? (
                              <span className="text-xs text-[#666]">{variant.variant_name}</span>
                            ) : (
                              <span className="text-[#999] text-xs">—</span>
                            )}
                          </div>
                          <div className="col-span-1">
                            <span className="text-xs text-[#999]">#{variant.id}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="font-semibold text-[#2C2C2C] text-sm">
                              ${parseFloat(variant.price ?? 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="col-span-2 flex justify-end space-x-2">
                            <button
                              onClick={() => handleEditVariant(variant)}
                              className="bg-[#2C2C2C] text-white py-1.5 px-3 rounded hover:bg-[#1a1a1a] transition-colors text-xs font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(variant)}
                              className="bg-red-500 text-white py-1.5 px-3 rounded hover:bg-red-600 transition-colors text-xs font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
